import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { CATEGORY_META } from "../lib/categories";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { BusinessCard } from "../components/BusinessCard";
import { ArrowRight, Search, Sparkles, BadgeCheck, BarChart3, TrendingUp } from "lucide-react";
import Seo from "../components/Seo";

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [query, setQuery] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/businesses/featured", { params: { limit: 6 } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  const submit = (e) => {
    e.preventDefault();
    nav(`/search?q=${encodeURIComponent(query)}`);
  };

  return (
    <div data-testid="home-page">
      <Seo
        title="BizzAtlas — The Global B2B Directory"
        description="Find vetted agencies, software vendors, contractors, attorneys, and more across 19 B2B industries."
        keywords={["B2B directory", "Yellow Pages", "business listings", "service providers"]}
        canonicalPath="/"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "BizzAtlas",
          potentialAction: {
            "@type": "SearchAction",
            target: { "@type": "EntryPoint", urlTemplate: `${process.env.REACT_APP_BACKEND_URL}/search?q={search_term_string}` },
            "query-input": "required name=search_term_string",
          },
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none [mask-image:radial-gradient(ellipse_60%_50%_at_50%_30%,black,transparent)]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8 pt-24 pb-20">
          <div className="max-w-4xl">
            <div className="reveal inline-flex items-center gap-2 text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-500 mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
              19 industries · 100% verified providers
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-zinc-900 reveal-2">
              The directory<br />
              built for <span className="italic text-zinc-500">B2B buyers</span>.
            </h1>
            <p className="mt-6 text-lg text-zinc-600 max-w-2xl reveal-3">
              Find vetted agencies, software vendors, contractors, attorneys and more — across 19 categories — all in one place.
            </p>

            <form onSubmit={submit} className="mt-10 reveal-4 max-w-2xl">
              <div className="flex items-center gap-2 rounded-xl border border-zinc-300 bg-white p-1.5 shadow-[0_1px_0_rgba(10,15,30,0.04)] focus-within:border-zinc-900 transition">
                <Search className="h-5 w-5 ml-3 text-zinc-400" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Try ‘React agency’, ‘SOC 2 audit’, ‘CFO services’…"
                  className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 h-12 text-base"
                  data-testid="home-search-input"
                />
                <Button type="submit" className="h-10 btn-brand" data-testid="home-search-submit">
                  Search <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
              <div className="mt-4 text-xs text-zinc-500 flex items-center flex-wrap gap-x-3 gap-y-1">
                <span className="font-mono-tag uppercase tracking-[0.18em] text-zinc-400">Popular:</span>
                {["IT services", "Legal", "Marketing", "Cybersecurity", "Recruiting"].map((t) => (
                  <button key={t} type="button" onClick={() => nav(`/search?q=${encodeURIComponent(t)}`)} className="hover:text-zinc-900 link-underline">{t}</button>
                ))}
              </div>
            </form>
          </div>

          {/* Marquee strip */}
          <div className="mt-20 -mx-6 lg:-mx-8 border-y border-zinc-200 bg-zinc-50/60 overflow-hidden">
            <div className="flex marquee whitespace-nowrap py-4">
              {[...categories, ...categories].map((c, i) => {
                const meta = CATEGORY_META[c.slug] || {};
                const Icon = meta.Icon;
                return (
                  <div key={`${c.slug}-${i}`} className="flex items-center gap-2 px-6 text-sm text-zinc-500">
                    {Icon && <Icon className="h-4 w-4" style={{ color: meta.accent }} />}
                    <span>{c.name}</span>
                    <span className="text-zinc-300">·</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-2">/ Browse</div>
            <h2 className="font-display text-4xl tracking-tight">19 industries. One directory.</h2>
          </div>
          <Link to="/categories" className="text-sm text-zinc-600 hover:text-zinc-900 link-underline" data-testid="home-view-all-categories">View all →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {categories.slice(0, 12).map((c, idx) => {
            const meta = CATEGORY_META[c.slug] || {};
            const Icon = meta.Icon;
            return (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                data-testid={`home-cat-${c.slug}`}
                className="card-lift rounded-lg border border-zinc-200 bg-white p-5 group reveal"
                style={{ animationDelay: `${Math.min(idx * 25, 280)}ms` }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="h-10 w-10 rounded-md grid place-items-center text-white" style={{ background: meta.accent || "#0F172A" }}>
                    {Icon && <Icon className="h-5 w-5" />}
                  </div>
                  <span className="font-mono-tag text-xs text-zinc-400">{c.count}</span>
                </div>
                <div className="font-medium text-zinc-900 leading-tight">{c.name}</div>
                <div className="text-xs text-zinc-500 mt-1 group-hover:text-zinc-700 transition">Explore providers →</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 lg:px-8 py-12">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-2">/ Featured</div>
              <h2 className="font-display text-4xl tracking-tight flex items-center gap-3">Spotlight providers <Sparkles className="h-7 w-7 text-amber-500" /></h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {featured.map((b, i) => <BusinessCard b={b} idx={i} key={b.id} />)}
          </div>
        </section>
      )}

      {/* VALUE STRIP */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { Icon: BadgeCheck, title: "Verified listings", body: "Paid memberships unlock a verified badge, multi-image galleries, and project links." },
            { Icon: BarChart3,  title: "Built-in analytics", body: "Track views, clicks and engagement time on every listing with real-time dashboards." },
            { Icon: TrendingUp, title: "Promote & rank up", body: "Use ad credits to sponsor your listing, get pinned to category pages, and surface in featured." },
          ].map((card, i) => (
            <div key={card.title} className="rounded-xl border border-zinc-200 p-8 bg-zinc-50/40 reveal" style={{ animationDelay: `${i * 80}ms` }}>
              <card.Icon className="h-6 w-6 text-zinc-900" />
              <div className="mt-6 font-display text-2xl">{card.title}</div>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed">{card.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8 pb-24">
        <div className="relative overflow-hidden rounded-2xl surface-navy px-10 py-16">
          <div className="absolute inset-0 bg-grain opacity-30" />
          <div className="relative max-w-2xl">
            <Badge className="bg-white/10 text-white border-white/10">For business owners</Badge>
            <h3 className="mt-4 font-display text-4xl tracking-tight">List your business in 60 seconds.</h3>
            <p className="mt-3 text-zinc-300 max-w-xl">Free forever. Upgrade anytime to unlock the verified badge, image galleries, and promoted placement.</p>
            <div className="mt-8 flex items-center gap-3">
              <Button asChild size="lg" className="bg-white text-zinc-900 hover:bg-zinc-200" data-testid="home-cta-list">
                <Link to="/list-business">List your business</Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="text-white hover:bg-white/10 hover:text-white" data-testid="home-cta-pricing">
                <Link to="/pricing">See pricing</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
